import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  IconButton
} from "@mui/material";
import { useParams } from "react-router-dom";
import { ApiServices } from "../../utils/ApiService";
import { useSnackbar } from "notistack";
import { DataGrid } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import Download from "@mui/icons-material/Download";
import JSZip from "jszip";

export default function AdminFileView() {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [pdfLoading, setPdfLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePath, setFilePath] = useState([]);
  const [downloadCache, setDownloadCache] = useState({});
  const [downloadingFiles, setDownloadingFiles] = useState({});

  const fetchFilePaths = async () => {
   setPdfLoading(true);

   try {
      const res = await ApiServices.get(`/file/file-paths/${id}`);

      if (res.status === 200) {
        setFilePath(res.data);
      }
    } catch (err) {
      console.error(err);
      let errorMessage = "Something went wrong.";

      if (err.response) {
        switch (err.response.status) {
          case 400:
            errorMessage = err.response.data?.message || "Bad Request.";
            break;

          case 401:
            errorMessage = "Unauthorized.";
            break;

          case 403:
            errorMessage = "Access denied.";
            break;

          case 404:
            errorMessage = err.response.data?.message || "Files not found.";
            break;

          case 500:
            errorMessage = "Internal server error.";
            break;

          default:
            errorMessage =
              err.response.data?.message ||
              `Error ${err.response.status}`;
        }
      } else if (err.request) {
        errorMessage = "Unable to reach server.";
      } else {
        errorMessage = err.message;
      }
      enqueueSnackbar(errorMessage, { variant: "error",});
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedFile) return;

    const filePath = `${selectedFile.documentCategory}/${selectedFile.documentName}`;
    try {
      setDeleteLoading(true);

      const res = await ApiServices.put(`/file/file-path/update/${id}`, {}, { params: { filePath }});

      if (res.status === 200) {
        enqueueSnackbar("File deleted successfully.", {
          variant: "success",
        });

        setFilePath((prev) =>
          prev.filter((item) => item.id !== selectedFile.id)
        );
      }
    } catch (err) {
      console.error(err);

      enqueueSnackbar("Failed to delete file.", {
        variant: "error",
      });
    } finally {
      setDeleteLoading(false);
      handleCancel();
    }
  };

  const handleDownload = async (file) => {
    const cacheKey = `${file.documentCategory}/${file.documentName}`;

    // Already downloading
    if (downloadingFiles[cacheKey]) return;

    setDownloadingFiles((prev) => ({
      ...prev,
      [cacheKey]: true,
    }));

    try {
      // If already cached
      const cached = downloadCache[cacheKey];

      if (cached) {
        const link = document.createElement("a");

        link.href = cached.url;
        link.download = cached.fileName;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        return;
      }

      const response = await ApiServices.get(
        `/file/fetch-file/${id}`,
        {
          params: {
            filePath: cacheKey,
          },
          responseType: "blob",
        }
      );

      const zip = await JSZip.loadAsync(response.data);

      const zipEntry = Object.values(zip.files).find((f) => !f.dir);

      if (!zipEntry) {
        enqueueSnackbar("No file found.", {
          variant: "error",
        });
        return;
      }

      let mimeType = "application/octet-stream";

      if (zipEntry.name.toLowerCase().endsWith(".pdf")) {
        mimeType = "application/pdf";
      } else if (zipEntry.name.toLowerCase().endsWith(".docx")) {
        mimeType =
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      }

      const buffer = await zipEntry.async("arraybuffer");

      const blob = new Blob([buffer], {
        type: mimeType,
      });

      const url = URL.createObjectURL(blob);

      setDownloadCache((prev) => ({
        ...prev,
        [cacheKey]: {
          url,
          fileName: zipEntry.name.split("/").pop(),
        },
      }));

      const link = document.createElement("a");

      link.href = url;
      link.download = zipEntry.name.split("/").pop();

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);

      enqueueSnackbar("Failed to download file.", {
        variant: "error",
      });
    } finally {
      setDownloadingFiles((prev) => ({
        ...prev,
        [cacheKey]: false,
      }));
    }
  };

  const openDeleteModal = (file) => {
    setSelectedFile(file);
    setModalOpen(true);
  };

  const handleCancel = () => {
    setModalOpen(false);
    setSelectedFile(null);
  };

  const queryColumns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.6,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "documentCategory",
      headerName: "Document Category",
      flex: 1,
      align: "center",
      headerAlign: "center"
    },
    {
      field: "documentName",
      headerName: "Document Name",
      flex: 1.8,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.8,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
          }}
        >
          <IconButton
            color="primary"
            size="small"
            disabled={
              downloadingFiles[
                `${params.row.documentCategory}/${params.row.documentName}`
              ]
            }
            onClick={() => handleDownload(params.row)}
          >
            {downloadingFiles[
              `${params.row.documentCategory}/${params.row.documentName}`
            ] ? (
              <CircularProgress size={20} />
            ) : (
              <Download />
            )}
          </IconButton>

          {isSuperAdmin && (
            <IconButton
              color="error"
              size="small"
              onClick={() => openDeleteModal(params.row)}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("user"));

    setIsSuperAdmin(userData?.role === "Super Admin");

    fetchFilePaths();
  }, []);

  useEffect(() => {
    return () => {
      Object.values(downloadCache).forEach((item) => {
        URL.revokeObjectURL(item.url);
      });
    };
  }, []);

  return (
    <>
      <Box sx={{ width: "100%", pt: "0" }}>
        <Box sx={{ display: "block"}}>
          <Grid container spacing={2} sx={{ width: "100%" }}>
            <Grid item xs={12} md={6} sx={{ width: "100%" }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ flexGrow: 1 }}>
                  Manage Files
                </Typography>
              </Box>

              {/* 🔹 GRID + LOADER OVERLAY */}
              {pdfLoading ? (
                <Grid
                  container
                  justifyContent="center"
                  alignItems="center"
                  style={{ height: "100%" }}
                >
                  <CircularProgress />
                </Grid>
                ) : (
                <Box sx={{ width: "100%" }}>
                  <DataGrid
                    rows={filePath}
                    // getRowId={(row) => row}
                    columns={queryColumns}
                    pageSize={5}
                    rowsPerPageOptions={[5]}
                    disableSelectionOnClick
                    getRowHeight={() => "auto"}
                    getColumnHeaderHeight={() => "auto"} // ✅ IMPORTANT
                    sx={{
                      "& .MuiDataGrid-columnHeaderTitle": {
                        whiteSpace: "normal", // allow wrap
                        lineHeight: 1.2,
                        textAlign: "center",
                        fontWeight: 600,
                      },
                      "& .MuiDataGrid-columnHeader": {
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "8px",
                      },
                    }}
                  />
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Modal for Delete */}
            <Dialog
              open={modalOpen}
              onClose={!deleteLoading ? handleCancel : undefined}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle>Delete File</DialogTitle>

              <DialogContent>
                <DialogContentText>
                  Are you sure you want to delete
                  <br />
                  <br />
                  <strong>{selectedFile?.documentName}</strong>?
                </DialogContentText>
              </DialogContent>

              <DialogActions>
                <Button
                  onClick={handleCancel}
                  color="inherit"
                  disabled={deleteLoading}
                >
                  Cancel
                </Button>

                <Button
                  color="error"
                  variant="contained"
                  onClick={handleDelete}
                  disabled={deleteLoading}
                  startIcon={
                    deleteLoading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <DeleteIcon />
                    )
                  }
                >
                  {deleteLoading ? "Deleting..." : "Yes, Delete"}
                </Button>
              </DialogActions>
            </Dialog>
    </>
  );
}
